/**
 * Seeds representative gap analysis + published protocol for Sarah Johnson
 * so the Notifications page shows demo question cards and check-in cards.
 * Always runs to ensure fresh demo data on reload.
 */
import { loadGapAnalyses, saveGapAnalysis, updateGapAnalysis } from './patientGapAnalysisStore';
import type { PatientGapAnalysis } from './patientGapAnalysisStore';
import { loadPublishedProtocols, savePublishedProtocol, updatePublishedProtocol } from './publishedProtocolsStore';
import type { PublishedProtocol } from './publishedProtocolsStore';

// Must match the id in src/mocks/users.json patients[0]
const MOCK_PATIENT_ID = 'patient-001';
const MOCK_PATIENT_NAME = 'Sarah Johnson';

const SEED_ANALYSIS: PatientGapAnalysis = {
  id: 'seed-analysis-001',
  patientId: MOCK_PATIENT_ID,
  patientName: MOCK_PATIENT_NAME,
  createdDate: '2026-04-20T10:00:00.000Z',
  questions: [
    {
      id: 'seed-q-001',
      domain: 'CLINICAL',
      gapKey: 'hba1c_trend',
      gapLabel: 'HbA1c Trend',
      gapStatus: 'MISSING',
      severity: 'HIGH',
      question: 'When was your last HbA1c test, and what was the result?',
      rationale: 'HbA1c trend directly impacts glycemic management targets.',
      included: true,
      sharedWithPatient: true,
      sharedAt: '2026-04-21T09:00:00.000Z',
      patientStatus: 'PENDING_PATIENT',
      thread: [],
    },
    {
      id: 'seed-q-002',
      domain: 'CLINICAL',
      gapKey: 'medication_adherence',
      gapLabel: 'Medication Adherence',
      gapStatus: 'MISSING',
      severity: 'HIGH',
      question:
        'Are you taking all prescribed medications consistently, and have there been any missed doses recently?',
      rationale: 'Medication gaps are a leading cause of CGM outlier readings.',
      included: true,
      sharedWithPatient: true,
      sharedAt: '2026-04-21T09:00:00.000Z',
      patientStatus: 'PENDING_PATIENT',
      thread: [],
    },
    {
      id: 'seed-q-003',
      domain: 'CULTURAL',
      gapKey: 'religious_fasting',
      gapLabel: 'Religious / Cultural Fasting',
      gapStatus: 'LOW_CONF',
      severity: 'HIGH',
      question:
        'Do you observe any religious or cultural fasting periods (e.g., Ramadan, Lent, Yom Kippur)?',
      rationale: 'Fasting periods require protocol adjustment to prevent hypoglycemia.',
      included: true,
      sharedWithPatient: true,
      sharedAt: '2026-04-21T09:00:00.000Z',
      patientStatus: 'PENDING_PATIENT',
      thread: [],
    },
    {
      id: 'seed-q-004',
      domain: 'FAMILY',
      gapKey: 'family_history_diabetes',
      gapLabel: 'Family History of Diabetes',
      gapStatus: 'MISSING',
      severity: 'HIGH',
      question:
        'Do any first-degree relatives (parents, siblings, children) have Type 2 diabetes or prediabetes?',
      rationale: 'Family history is a key risk stratification factor for protocol intensity.',
      included: true,
      sharedWithPatient: true,
      sharedAt: '2026-04-21T09:00:00.000Z',
      patientStatus: 'PENDING_PATIENT',
      thread: [],
    },
    {
      id: 'seed-q-005',
      domain: 'SDOH',
      gapKey: 'food_security',
      gapLabel: 'Food Security',
      gapStatus: 'MISSING',
      severity: 'HIGH',
      question:
        'Do you have consistent access to the foods recommended in your protocol, or are cost or availability barriers a concern?',
      rationale: 'Food insecurity requires protocol adaptation to realistic, accessible options.',
      included: true,
      sharedWithPatient: true,
      sharedAt: '2026-04-21T09:00:00.000Z',
      patientStatus: 'PENDING_PATIENT',
      thread: [],
    },
    {
      id: 'seed-q-006',
      domain: 'BEHAVIORAL',
      gapKey: 'sleep_patterns',
      gapLabel: 'Sleep Patterns',
      gapStatus: 'STALE',
      severity: 'MEDIUM',
      question: 'How many hours of sleep do you typically get per night, and do you wake frequently?',
      rationale: 'Poor sleep elevates cortisol and disrupts insulin sensitivity.',
      included: true,
      sharedWithPatient: true,
      sharedAt: '2026-04-21T09:00:00.000Z',
      patientStatus: 'PENDING_PATIENT',
      thread: [],
    },
  ],
};

const SEED_PROTOCOL: PublishedProtocol = {
  id: 'seed-protocol-001',
  patientId: MOCK_PATIENT_ID,
  patientName: MOCK_PATIENT_NAME,
  title: 'Personalized Nutrition for CGM Management',
  description: 'Customized protocol addressing HbA1c trends and glycemic variability.',
  templateId: 'template-cgm-001',
  status: 'ACTIVE',
  sharedWithPatient: true,
  createdDate: '2026-04-20T08:00:00.000Z',
  startDate: '2026-04-21T00:00:00.000Z',
  templateNotes: [
    'Monitor meal timing and portion sizes',
    'Track daily CGM readings',
    'Report weekly adherence progress',
  ],
  classifications: [
    { id: 'class-1', customFoodName: 'Leafy Greens', classification: 'GREEN', portion: 'Unlimited', notes: 'Low glycemic impact' },
    { id: 'class-2', customFoodName: 'Whole Grains', classification: 'YELLOW', portion: '1 cup per meal', notes: 'Monitor portion size' },
    { id: 'class-3', customFoodName: 'Refined Sugars', classification: 'RED', portion: 'Minimize', notes: 'Avoid when possible' },
  ],
  rules: [
    {
      id: 'rule-1',
      ruleType: 'MEAL_TIMING',
      description: 'Eat meals at consistent times (7am, 12pm, 6pm)',
      parameters: { breakfastTime: '07:00', lunchTime: '12:00', dinnerTime: '18:00' },
      isActive: true,
    },
    {
      id: 'rule-2',
      ruleType: 'PAIRING',
      description: 'Pair carbs with protein/fat to minimize blood sugar spikes',
      parameters: { proteinGramPerMeal: 20, fatGramPerMeal: 10 },
      isActive: true,
    },
  ],
  checkInTasks: [
    { id: 'task-d1', frequency: 'daily', taskType: 'CGM_UPLOAD', label: 'Upload CGM Reading', description: 'Share your latest CGM sensor reading or screenshot', required: true },
    { id: 'task-d2', frequency: 'daily', taskType: 'MEAL_PHOTO', label: 'Log a Meal Photo', description: 'Photo of your largest meal of the day', required: false },
    { id: 'task-w1', frequency: 'weekly', taskType: 'WEIGHT', label: 'Record Your Weight', description: 'Morning weight before eating', required: true },
    { id: 'task-w2', frequency: 'weekly', taskType: 'BLOOD_PRESSURE', label: 'Blood Pressure Reading', description: 'Sit quietly 5 min before measuring', required: true },
    { id: 'task-w3', frequency: 'weekly', taskType: 'CGM_UPLOAD', label: 'CGM Weekly Trend', description: 'Upload your full week CGM trend report', required: true },
    { id: 'task-m1', frequency: 'monthly', taskType: 'SYMPTOM_LOG', label: 'Monthly Symptom Review', description: 'Energy levels, digestion, and any new symptoms', required: true },
    { id: 'task-m2', frequency: 'monthly', taskType: 'WEIGHT', label: 'Monthly Weight Trend', description: 'Monthly weight for trend analysis', required: true },
    { id: 'task-m3', frequency: 'monthly', taskType: 'BLOOD_PRESSURE', label: 'Monthly BP Review', description: 'Review your BP readings for the month', required: false },
  ],
};

export const seedMockGapDataIfEmpty = (): void => {
  // Always upsert the seed analysis so stale localStorage data is replaced
  const existing = loadGapAnalyses();
  const hasSeedAnalysis = existing.some((a) => a.id === SEED_ANALYSIS.id);
  if (hasSeedAnalysis) {
    updateGapAnalysis(SEED_ANALYSIS.id, SEED_ANALYSIS);
  } else {
    saveGapAnalysis(SEED_ANALYSIS);
  }

  // Always upsert seed protocol so checkInTasks stay current
  const protocols = loadPublishedProtocols();
  const hasSeedProtocol = protocols.some((p) => p.id === SEED_PROTOCOL.id);
  if (hasSeedProtocol) {
    updatePublishedProtocol(SEED_PROTOCOL.id, SEED_PROTOCOL);
  } else {
    savePublishedProtocol(SEED_PROTOCOL);
  }
};
